import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { Image as ImageIcon, MapPin } from 'lucide-react';
import { preloadMedia } from '@/lib/preloadMedia';

interface FeatureImageProps {
  municipalityId: string;
  alt: string;
  label: string;
  fallbackImage?: string;
  onClick?: () => void;
}

interface FeatureCarouselImage {
  id: string;
  image_url: string;
  display_order: number;
}

export const FeatureImage = ({
  municipalityId,
  alt,
  label,
  fallbackImage,
  onClick,
}: FeatureImageProps) => {
  const [images, setImages] = useState<FeatureCarouselImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isVisible = useIntersectionObserver(containerRef, {
    threshold: 0.1,
    rootMargin: '100px',
  });

  const visibleImages = useMemo<FeatureCarouselImage[]>(() => {
    if (images.length > 0) return images;
    if (!fallbackImage) return [];
    return [{
      id: 'municipality-hero-fallback',
      image_url: fallbackImage,
      display_order: 0,
    }];
  }, [images, fallbackImage]);

  useEffect(() => {
    let disposed = false;
    let generation = 0;
    const loadImages = async () => {
      const currentGeneration = ++generation;
      setLoading(true);
      setImagesPreloaded(false);
      try {
        const data = await api.carouselImages.list(municipalityId);
        if (disposed || currentGeneration !== generation) return;
        setImages((data || []).filter((img: FeatureCarouselImage & { is_hidden?: boolean }) => !img.is_hidden));
      } catch (error) {
        if (disposed || currentGeneration !== generation) return;
        console.error('Error loading feature carousel images:', error);
        setImages([]);
      } finally {
        if (!disposed && currentGeneration === generation) setLoading(false);
      }
    };

    void loadImages();
    const handleFocus = () => void loadImages();
    window.addEventListener('focus', handleFocus);
    return () => {
      disposed = true;
      window.removeEventListener('focus', handleFocus);
    };
  }, [municipalityId]);

  useEffect(() => {
    if (!isVisible || visibleImages.length === 0 || imagesPreloaded) return;

    const controller = new AbortController();
    const preloadPromises = visibleImages.map(img => preloadMedia(img.image_url, 'image', controller.signal));
    void Promise.all(preloadPromises).then(() => {
      if (!controller.signal.aborted) setImagesPreloaded(true);
    });
    return () => controller.abort();
  }, [isVisible, visibleImages, imagesPreloaded]);

  if (loading || visibleImages.length === 0) {
    return (
      <div ref={containerRef} className="bottom-image-container feature-placeholder">
        <div className="feature-placeholder-content">
          <ImageIcon />
          <p>{loading ? 'Cargando...' : 'Sin imágenes configuradas'}</p>
        </div>
      </div>
    );
  }

  if (!imagesPreloaded) {
    return (
      <div ref={containerRef} className="bottom-image-container feature-placeholder">
        <div className="feature-placeholder-content">
          <div className="feature-spinner" />
          <p>Cargando imágenes...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bottom-image-container">
      <Carousel
        opts={{ align: 'start', loop: true }}
        plugins={[Autoplay({ delay: 5000, stopOnInteraction: false })]}
        className="w-full h-full"
      >
        <CarouselContent className="h-full">
          {visibleImages.map((image) => (
            <CarouselItem key={image.id} className="h-full">
              <img
                src={image.image_url}
                alt={alt}
                className="bottom-image"
                loading="eager"
                decoding="sync"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <button
        type="button"
        className={`feature-image-label${onClick ? '' : ' is-static'}`}
        onClick={onClick}
        disabled={!onClick}
      >
        <MapPin strokeWidth={2} />
        <span>{label}</span>
      </button>
    </div>
  );
};
